import type { PlanPrices } from "./types";

/**
 * Price arithmetic and formatting, deliberately free of React Native imports so
 * the money maths can be tested for real rather than asserted against source
 * text. Nothing here reaches a store or a device.
 */

/** The parts of a store product this module needs. */
export type StorePriceInput = {
  /** Numeric amount in the storefront's currency. */
  price: number;
  /** The store's own formatted string — always preferred for display. */
  priceString: string;
  /** ISO 4217. */
  currencyCode: string;
};

/**
 * Format an amount in ANY ISO-4217 currency. The device locale shapes the
 * FORMAT (separators, symbol placement); the currency itself is a fact about
 * where the buyer is buying, which only the store knows.
 */
export function formatAmountInCurrency(
  amount: number,
  currencyCode: string,
): string {
  const locale =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().locale
      : "en-US";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
}

/**
 * Build the displayed prices from the two store products.
 *
 * The saving is COMPUTED, never quoted from a constant. Apple converted the
 * annual to $69.99 in many storefronts while the monthly is $7.99 or $8.99, so
 * the same two products save 26% in the US and ~35% elsewhere; one hardcoded
 * percentage would be a false savings claim nearly everywhere.
 *
 * Returns null on any input that cannot produce an honest number — a missing
 * product, a non-finite amount, or a zero/negative price, which would otherwise
 * yield Infinity or NaN and render as "NaN% off" rather than failing.
 */
export function derivePlanPrices(
  monthly: StorePriceInput | undefined,
  annual: StorePriceInput | undefined,
): PlanPrices | null {
  if (!monthly || !annual) return null;
  if (!Number.isFinite(monthly.price) || !Number.isFinite(annual.price)) {
    return null;
  }
  if (monthly.price <= 0 || annual.price <= 0) return null;

  // The store formats its own price strings; only the derived per-month figure
  // is ours to write, and it uses the currency the store named.
  const currencyCode = annual.currencyCode;
  return {
    monthly: monthly.priceString,
    annual: annual.priceString,
    monthlyEquivalentAnnual: formatAmountInCurrency(
      annual.price / 12,
      currencyCode,
    ),
    savingsPercent: Math.round((1 - annual.price / (monthly.price * 12)) * 100),
    currencyCode,
  };
}
